import _objectSpread from "@babel/runtime/helpers/objectSpread";
import makePlainReducer from '../makePlainReducer';
/* Helper to ensure that each supported action has a metaKey param */

var checkMetaKey = function checkMetaKey(action) {
  if (!action.metaKey) {
    throw new Error("The meta reducer requires a metaKey property on each action it supports, " + action.type + " didn't have this!");
  }
};
/* Handles _START actions */


var onLoadStart = function onLoadStart(state, action) {
  var _objectSpread2;

  checkMetaKey(action);
  return _objectSpread({}, state, (_objectSpread2 = {}, _objectSpread2[action.metaKey] = _objectSpread({}, state[action.metaKey], {
    loading: true
  }), _objectSpread2));
};
/* Handles _SUCCESS actions */


var onLoadSuccess = function onLoadSuccess(state, action) {
  var _objectSpread3;

  checkMetaKey(action);
  return _objectSpread({}, state, (_objectSpread3 = {}, _objectSpread3[action.metaKey] = _objectSpread({}, state[action.metaKey], {
    loaded: true,
    loading: false,
    next: action.next || null
  }), _objectSpread3));
};
/* Handles _FAILURE actions */


var onLoadFailure = function onLoadFailure(state, action) {
  var _objectSpread4;

  checkMetaKey(action);
  return _objectSpread({}, state, (_objectSpread4 = {}, _objectSpread4[action.metaKey] = _objectSpread({}, state[action.metaKey], {
    loading: false,
    error: action.error
  }), _objectSpread4));
};
/**
 * Creates a `meta` reducer. A meta reducer stores all information about request
 * status for http actions. All keys for the reducer are generated using
 * the [makeMetaKey](#makemetakey) utility function.
 * * **Initial State**:
 * ```js static
 * {
 *   <model>: {
 *     loading: false,
 *     loaded: false,
 *     error: null,
 *     next: null,
 *   },
 *   <models>s: {
 *     loading: false,
 *     loaded: false,
 *     error: null,
 *     next: null,
 *   },
 * }
 * ```
 * * **Supported Actions**:
 *   * `GET_<MODEL>_START`: Sets `loading` to `true`.
 *     * `action.metaKey`: The key to store the meta information under
 *   * `GET_<MODEL>_FAILURE`: Sets `loading` to `false` and `error` to `action.error`
 *     * `action.metaKey`: The key to store the meta information under
 *     * `action.error`: The Error object that describes what went wrong.
 *   * `GET_<MODEL>_SUCCESS`: Sets `loading` to `false` and `loaded` to `true`
 *     * `action.metaKey`: The key to store the meta information under
 *   * `GET_<MODEL>S_START`: Sets `loading` to `true`.
 *     * `action.metaKey`: The key to store the meta information under
 *   * `GET_<MODEL>S_FAILURE`: Sets `loading` to `false` and `error` to `action.error`
 *     * `action.metaKey`: The key to store the meta information under
 *     * `action.error`: The Error object that describes what went wrong.
 *   * `GET_<MODEL>S_SUCCESS`: Sets `loading` to `false`, `loaded` to `true`,
 *     and `next` to `action.next`
 *     * `action.metaKey`: The key to store the meta information under
 *     * `action.next`: The paginated url to use for subsequent requests
 *   * `REMOVE_TOKEN`: Resets the reducer to the initial state.
 *
 * @param {Object} options The options for the creator function
 * @param {string} options.model The name of the model being used
 * @param {string} options.pluralModel The name of the plural model being used
 * @param {Object} options.customConfig Custom action handlers
 * @returns {Function} A newly created redux reducer
 *
 * @public
 * @author Connor Taylor
 *
 * @example
 * import { combineReducers } from 'redux';
 *
 * const reducer = combineReducers({
 *   meta: makeMetaReducer({
 *     model: `article`,
 *     customConfig: {
 *       CUSTOM_ACTION: (state, action) => ({ foo: `bar` }),
 *     },
 *   }),
 * });
 *
 * // An example of what the meta reducer state could look like
 * {
 *   article: {
 *     meta: {
 *       article: {
 *         loading: false,
 *         loaded: false,
 *         error: null,
 *         next: null
 *       }
 *       articles: {
 *         loading: false,
 *         loaded: true,
 *         error: null,
 *         next: `http://api.foo.com/articles?page=2`
 *       },
 *       'articles?featured=true': {
 *         loading: false,
 *         loaded: true,
 *         error: null,
 *         next: `http://api.foo.com/articles?featured=true&page=2`
 *       }
 *     }
 *   }
 * }

 */


var makeMetaReducer = function makeMetaReducer(_ref) {
  var _defaultInitialState, _objectSpread5;

  var model = _ref.model,
      _ref$pluralModel = _ref.pluralModel,
      pluralModel = _ref$pluralModel === void 0 ? null : _ref$pluralModel,
      _ref$customConfig = _ref.customConfig,
      customConfig = _ref$customConfig === void 0 ? {} : _ref$customConfig,
      _ref$initialState = _ref.initialState,
      initialState = _ref$initialState === void 0 ? null : _ref$initialState;
  var upperModel = model.toUpperCase();
  var pluralVersion = pluralModel || model + "s";
  var upperPluralModel = pluralVersion.toUpperCase();
  var defaultInitialState = (_defaultInitialState = {}, _defaultInitialState[model] = {
    loading: false,
    loaded: false,
    error: null,
    next: null
  }, _defaultInitialState[pluralVersion] = {
    loading: false,
    loaded: false,
    error: null,
    next: null
  }, _defaultInitialState);
  return makePlainReducer({
    model: model,
    config: _objectSpread((_objectSpread5 = {}, _objectSpread5["GET_" + upperModel + "_START"] = onLoadStart, _objectSpread5["GET_" + upperPluralModel + "_START"] = onLoadStart, _objectSpread5["GET_" + upperModel + "_SUCCESS"] = onLoadSuccess, _objectSpread5["GET_" + upperPluralModel + "_SUCCESS"] = onLoadSuccess, _objectSpread5["GET_" + upperModel + "_FAILURE"] = onLoadFailure, _objectSpread5["GET_" + upperPluralModel + "_FAILURE"] = onLoadFailure, _objectSpread5.REMOVE_TOKEN = function REMOVE_TOKEN() {
      return initialState || defaultInitialState;
    }, _objectSpread5), customConfig),
    initialState: initialState || defaultInitialState
  });
};
/** @component */


export default makeMetaReducer;